import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiArrowLeft, HiCheck } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";
import "./EditPaymentMethod.scss";

export const EditPaymentMethod: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['paymentMethods', 'common']);
  const { token } = useAuth();

  const [formData, setFormData] = useState({ name: "", percent: 0, id: id });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Fetch existing item
  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await adminApi.call(`/paymentmethod/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('paymentMethods:messages.fetchError'));
          return;
        }

        setFormData(response.data);
      } catch (error: any) {
        showErrorToast(t('paymentMethods:messages.fetchError') + ": " + error.message);
      }
    };

    fetchItem();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'percent' ? Number(value) : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    if (!formData.name?.trim()) {
      newErrors.name = t('paymentMethods:validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('paymentMethods:validation.nameMinLength');
    }
    if (formData.percent === null || formData.percent === undefined || formData.percent < 0 || formData.percent > 100) {
      newErrors.percent = t('paymentMethods:validation.percentMaxValue');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitUpdate = async () => {
    if (!validateForm()) {
      showErrorToast(t('paymentMethods:validation.fillAllFields'));
      return;
    }
    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/paymentmethod/admin/Update', 'PUT', formData);

      if (!response.success) {
        showErrorToast(response.error || t('paymentMethods:messages.updateError'));
        return;
      }

      showSuccessToast(t('paymentMethods:messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('paymentMethods:messages.updateError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('paymentMethods:breadcrumbs.paymentMethods'), path: '/payment-methods' },
    { label: t('paymentMethods:breadcrumbs.edit') }
  ];

  return (
    <PageLayout
      title={t('paymentMethods:editPaymentMethod')}
      subtitle={t('paymentMethods:subtitle')}
      breadcrumbs={breadcrumbs}
    >
      <div className="modern-form">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('paymentMethods:fields.name')} <span className="required">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('paymentMethods:placeholders.name')}
              required
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('paymentMethods:fields.percent')} <span className="required">*</span></label>
            <input
              type="number"
              className={`form-control ${errors.percent ? 'is-invalid' : ''}`}
              name="percent"
              value={formData.percent}
              onChange={handleInputChange}
              placeholder={t('paymentMethods:placeholders.percent')}
              min="0"
              max="100"
              required
            />
            {errors.percent && <div className="invalid-feedback">{errors.percent}</div>}
          </div>
        </div>

        <div className="form-actions">
          <ActionButton
            variant="primary"
            icon={HiCheck}
            onClick={submitUpdate}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('paymentMethods:buttons.updating') : t('paymentMethods:buttons.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
